// Shore fire-support targets: gun batteries, bunker complexes, supply dumps —
// placed on the terrain heightfield, called in by a spotter, and designatable
// through the exact same fire-control chain as a ship (they expose the same
// pos/heading/speed/sunk/tryHit interface the rangekeeper already speaks).
// Batteries can shoot back: real ballistic rounds through the shared shell
// simulator, flagged `light` so the armor scheme treats them as the 130 mm
// splinter threat they were, not 8-inch cruiser fire.

import * as THREE from "three";
import { solveElevation, randn } from "./guns.js";

const KINDS = {
  battery: { hp: 3.0, radius: 60, label: "ARTILLERY BATTERY" },
  coastal: { hp: 2.5, radius: 55, label: "COASTAL DEFENSE BATTERY" },
  bunker:  { hp: 5.0, radius: 50, label: "BUNKER COMPLEX" },
  dump:    { hp: 1.5, radius: 80, label: "SUPPLY DUMP" },
};

export class ShoreTarget {
  constructor(G, opts) {
    this.G = G;
    this.kind = opts.kind;
    const def = KINDS[this.kind];
    this.name = opts.name || def.label;
    this.desc = opts.desc || "";
    this.bdaText = opts.bdaText || "";
    this.pos = new THREE.Vector2(opts.x, opts.z);
    this.y = G.terrain ? G.terrain.heightAt(opts.x, opts.z) : 0;
    this.radius = def.radius;
    this.hp = def.hp;
    this.maxHp = def.hp;
    this.destroyed = false;
    this.called = false;       // spotter has passed it to CIC
    this.shore = true;
    // ship-target interface for the rangekeeper
    this.heading = 0; this.speed = 0; this.turnRate = 0;

    // counter-battery behavior
    this.shoots = !!opts.shoots;
    this.holdFire = true;      // released by the mission script
    this.gunTimer = 6 + Math.random() * 10;
    this.trackTime = 0;
    this.guns = opts.guns ?? 2;
    this.gunRange = opts.gunRange ?? 20000;

    this._smokeAcc = 0;
    this._flashAcc = 0;
    this.group = this.buildMesh();
    this.group.position.set(this.pos.x, this.y, this.pos.y);
    G.scene.add(this.group);
  }

  get sunk() { return this.destroyed; }

  buildMesh() {
    const g = new THREE.Group();
    const mat = (c) => new THREE.MeshLambertMaterial({ color: c });
    const box = (w, h, d, c, x, y, z) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(c));
      m.position.set(x, y, z);
      g.add(m);
      return m;
    };
    if (this.kind === "battery" || this.kind === "coastal") {
      // revetted gun pits: earth ring, gun blocks, barrels toward the sea
      for (const [ox, oz] of [[-14, -10], [10, -14], [-8, 14], [14, 10]]) {
        box(10, 2.2, 10, 0x6b5b40, ox, 1.1, oz);
        box(4, 2.4, 4, 0x4a4f42, ox, 2.6, oz);
        const b = box(9, 0.8, 0.8, 0x3a3f38, ox - 4.5 * (this.G.terrain?.dir ?? 1), 3.6, oz);
        b.rotation.z = 0.2 * (this.G.terrain?.dir ?? 1);
      }
    } else if (this.kind === "bunker") {
      box(26, 4, 18, 0x6f6a5c, 0, 2, 0);
      box(14, 3, 10, 0x625d50, 6, 5.5, 4);
      box(8, 2.5, 8, 0x6b5b40, -12, 1.2, -8);
    } else if (this.kind === "dump") {
      for (let i = 0; i < 9; i++)
        box(6, 3, 4, i % 2 ? 0x5c5744 : 0x51584a, (i % 3 - 1) * 9, 1.5, ((i / 3 | 0) - 1) * 7);
    }
    return g;
  }

  // Our shell burst at `at` (world, y = terrain height there).
  tryHit(at) {
    if (this.destroyed) return null;
    const d = Math.hypot(at.x - this.pos.x, at.z - this.pos.y);
    if (d > this.radius + 30) return null;
    const dmg = d < 22 ? 1.5 : d < this.radius ? 0.8 : 0.35;
    this.hp -= dmg;
    if (this.hp <= 0) {
      this.destroy();
      return { text: "TARGET DESTROYED", destroyed: true };
    }
    return { text: d < 22 ? "DIRECT HIT" : "EFFECT ON TARGET" };
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    const G = this.G;
    const at = new THREE.Vector3(this.pos.x, this.y + 4, this.pos.y);
    const big = this.kind === "dump";
    G.worldFx.spawn(at, {
      count: big ? 90 : 45, speed: big ? 55 : 30, life: 1.4, size: big ? 16 : 9, jitter: 12,
      colors: [0xffd890, 0xff8a3c, 0xff5a28, 0x2c2c2c], drag: 1.2, gravity: -6, grow: 2.5,
    });
    for (const m of this.group.children) {
      m.rotation.set((Math.random() - 0.5) * 0.6, Math.random() * 3, (Math.random() - 0.5) * 0.6);
      m.position.y *= 0.4;
      if (m.material) m.material.color.multiplyScalar(0.35);
    }
    const range = Math.hypot(this.pos.x - G.shipWorldPos().x, this.pos.y - G.shipWorldPos().z);
    setTimeout(() => G.audio.distantGuns(), Math.min(20, range / 340) * 1000);
  }

  update(dt) {
    const G = this.G;
    if (this.destroyed) {
      // burn for a good while — the pall is the BDA
      this._smokeAcc += dt;
      if (this._smokeAcc > 0.22) {
        this._smokeAcc = 0;
        G.worldFx.spawn(new THREE.Vector3(
          this.pos.x + (Math.random() - 0.5) * 24, this.y + 6, this.pos.y + (Math.random() - 0.5) * 24), {
          count: 2, speed: 2.5, life: 8, size: 10, jitter: 4,
          dir: new THREE.Vector3(0, 1, 0.2),
          colors: [0x1c1c1c, 0x3a342c, 0x54473a], gravity: 1.8, drag: 0.5, grow: 3.5,
        });
      }
      return;
    }
    if (!this.shoots || this.holdFire || G.over) return;

    const p = G.shipWorldPos();
    const range = Math.hypot(this.pos.x - p.x, this.pos.y - p.z);
    if (range > this.gunRange || !G.damage.afloat) {
      this.trackTime = Math.max(0, this.trackTime - dt);
      return;
    }
    this.trackTime += dt;
    this.gunTimer -= dt;
    if (this.gunTimer <= 0) {
      this.gunTimer = 26 + Math.random() * 18;
      this.fireAt(p, range);
    }
  }

  fireAt(p, range) {
    const G = this.G;
    // field guns against a moving ship: wild at first, never great
    const err = 950 * Math.max(0.3, 1 - this.trackTime / 260);
    const sol = solveElevation(range);
    const s = G.shipState;
    const aimX = p.x + Math.sin(s.heading) * s.speed * sol.tof + randn() * err;
    const aimZ = p.z + Math.cos(s.heading) * s.speed * sol.tof + randn() * err;
    for (let i = 0; i < this.guns; i++) {
      const tx = aimX + randn() * 160, tz = aimZ + randn() * 160;
      const dx = tx - this.pos.x, dz = tz - this.pos.y;
      const r = Math.hypot(dx, dz), b = Math.atan2(dx, dz);
      const es = solveElevation(r);
      const dir = new THREE.Vector3(
        Math.sin(b) * Math.cos(es.elev), Math.sin(es.elev), Math.cos(b) * Math.cos(es.elev));
      G.guns.spawnShell(new THREE.Vector3(this.pos.x, this.y + 4, this.pos.y),
        dir.multiplyScalar(650), { own: false, light: true, shooter: this });
    }
    // muzzle flashes on the shoreline + delayed report
    G.worldFx.spawn(new THREE.Vector3(this.pos.x, this.y + 4, this.pos.y), {
      count: 8, speed: 10, life: 0.5, size: 12, jitter: 10,
      colors: [0xffd890, 0xffb050], drag: 2 });
    setTimeout(() => G.audio.distantGuns(), Math.min(20, range / 340) * 1000);
    G.hud.log(`MUZZLE FLASHES ASHORE — ${this.name} FIRING, SPLASHES IN ${Math.round(solveElevation(range).tof)} S`, "bad");
  }
}
